import React, { useEffect } from "react";
import TextAnnotator from "components/util/helpers/TextAnnotator";
import l from "components/util/l";
import { useRevisionFormContext } from "components/brief/reviewer/revision/RevisionForm.lib.context";
import View from "components/brief/reviewer/revision/content/RevisionSections.view";

const RevisionSections = ({ currentSection }) => {

  const {
    proposal,
    currentAuthor,
    isDeveloper,
    allowComments,
    activeTabIndex,
    setActiveTabIndex,
    updateSection,
  } = useRevisionFormContext();

  const sectionIndex = currentSection.sourceIndex;
  const section = proposal.content[sectionIndex] || {};
  const visibleTabs = (section.tabs || [])
    .map((tab, index) => ({ ...tab, sourceIndex: index }))
    .filter((tab) => isDeveloper || tab.type !== "dev");
  const currentTab = visibleTabs.find((tab) => tab.sourceIndex === activeTabIndex) || visibleTabs[0] || {};
  const hasMockups = (section.mockups || []).length > 0;

  useEffect(() => {
    if (visibleTabs.length === 0) {
      if (activeTabIndex !== 0) setActiveTabIndex(0);
      return;
    }

    if (!visibleTabs.some((tab) => tab.sourceIndex === activeTabIndex))
      setActiveTabIndex(visibleTabs[0].sourceIndex);
  }, [activeTabIndex, visibleTabs, setActiveTabIndex]);

  if (visibleTabs.length === 0)
    return null;

  const visibleAnnotations = (section.annotations || [])
    .filter((annotation) => annotation.id)
    .filter((annotation) => isDeveloper || (annotation?.visibility_scope || "CLIENT") !== "DEV")
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const annotationNumbers = {};
  visibleAnnotations.forEach((annotation, index) => {
    annotationNumbers[annotation.id] = index + 1;
  });
  const hasPendingComments = visibleAnnotations.some((annotation) => !annotation?.is_solved);
  const approvalLabel = hasPendingComments
    ? l("Aprobar comentarios%PA642")
    : l("Aprobar contenido%PA641");

  const currentAnnotations = visibleAnnotations
    .filter((annotation) => annotation.type === "tab" && annotation.target_idx === currentTab.sourceIndex)
    .filter((annotation) => !annotation?.is_solved)
    .map((annotation) => ({
      id: annotation.id,
      text: annotation.text,
      author: annotation.author,
      start: annotation.start,
      end: annotation.end,
      order: annotation.order,
      visibility_scope: annotation?.visibility_scope || "CLIENT",
      is_solved: !!annotation?.is_solved,
    }));

  const onChangeCurrentAnnotations = (textAnnotatorAnnotations) => {
    if (!allowComments)
      return;

    updateSection(sectionIndex, (currentSectionData) => {
      const currentAnnotationsList = currentSectionData.annotations || [];
      const otherAnnotations = currentAnnotationsList.filter((annotation) => {
        const isCurrentTabAnnotation = annotation.type === "tab" && annotation.target_idx === activeTabIndex;
        if (!isCurrentTabAnnotation) return true;
        if (annotation?.is_solved) return true;
        return !(!isDeveloper && (annotation?.visibility_scope || "CLIENT") === "DEV");
      });
      const newTabAnnotations = (textAnnotatorAnnotations || []).map((annotation) => ({
        id: annotation.id,
        type: "tab",
        visibility_scope: annotation?.visibility_scope || "CLIENT",
        is_solved: !!annotation?.is_solved,
        target_idx: activeTabIndex,
        text: annotation.text,
        author: annotation.author || currentAuthor,
        order: annotation.order,
        start: annotation.start,
        end: annotation.end,
      }));

      return {
        ...currentSectionData,
        annotations: [...otherAnnotations, ...newTabAnnotations],
      };
    });
  }

  return (
    <View
      tabs={visibleTabs}
      activeTabIdx={activeTabIndex}
      activeSectionIdx={sectionIndex}
      currentTab={currentTab}
      currentAnnotations={currentAnnotations}
      annotationNumbers={annotationNumbers}
      approvalLabel={approvalLabel}
      showMockups={hasMockups}
      allowComments={allowComments}
      currentAuthor={currentAuthor}
      isDeveloper={isDeveloper}
      TextAnnotator={TextAnnotator}
      onClickSelectTab={setActiveTabIndex}
      onChangeCurrentAnnotations={onChangeCurrentAnnotations}
    />
  );

}

export default RevisionSections;
